import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { settingsAPI } from '../utils/api';
import { useAuth } from './AuthContext';

const SettingsContext = createContext(null);

const DEFAULTS = {
  hijriAdjustment: 0,
  notificationsEnabled: true,
  defaultReminderTime: '09:00',
  default_reminder_hour: 9,
  default_reminder_minute: 0,
  location: 'auto',
  timezone: 'auto',
  theme: 'system',
  language: 'en',
  persianCalendarEnabled: false,
  holidayCountries: [],
};

const LS_KEY = 'iremind_settings';
const HIJRI_AUTO_KEY = 'iremind_hijri_auto_v1'; // set once auto-adjust has been applied

// Timezone → default Hijri offset based on typical local moon sighting practices
const TIMEZONE_HIJRI_OFFSET = {
  // GCC — follow Saudi Umm al-Qura exactly (0)
  'Asia/Riyadh': 0, 'Asia/Kuwait': 0, 'Asia/Qatar': 0,
  'Asia/Bahrain': 0, 'Asia/Muscat': 0, 'Asia/Dubai': 0,
  // MENA (mostly follow Saudi, 0)
  'Asia/Istanbul': 0, 'Africa/Cairo': 0, 'Asia/Amman': 0,
  'Asia/Beirut': 0, 'Asia/Damascus': 0, 'Asia/Baghdad': 0,
  'Asia/Aden': 0, 'Asia/Sanaa': 0,
  'Africa/Casablanca': 0, 'Africa/Tripoli': 0, 'Africa/Tunis': 0,
  'Africa/Algiers': 0, 'Africa/Khartoum': 0,
  'Asia/Tehran': 0,
  // South Asia — local moon sighting typically 1 day after Saudi (-1)
  'Asia/Karachi': -1, 'Asia/Kolkata': -1, 'Asia/Colombo': -1,
  'Asia/Dhaka': -1, 'Asia/Kabul': -1, 'Asia/Kathmandu': -1,
  // Southeast Asia — mostly follow local sighting (-1)
  'Asia/Jakarta': -1, 'Asia/Makassar': -1, 'Asia/Jayapura': -1,
  'Asia/Kuala_Lumpur': -1, 'Asia/Singapore': -1,
  'Asia/Bangkok': -1, 'Asia/Manila': -1,
  // West Africa — varies, default 0
  'Africa/Lagos': 0, 'Africa/Dakar': 0, 'Africa/Abidjan': 0,
  'Africa/Accra': 0, 'Africa/Nairobi': 0, 'Africa/Addis_Ababa': 0,
};

function getHijriOffsetForTimezone(tz) {
  if (!tz) return 0;
  if (tz in TIMEZONE_HIJRI_OFFSET) return TIMEZONE_HIJRI_OFFSET[tz];
  // Broad fallback: Asian timezones not in GCC lean -1 (South/SE Asia)
  if (tz.startsWith('Asia/')) return -1;
  return 0;
}

function loadCached() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

function saveCache(settings) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(settings)); } catch { /* ignore */ }
}

export const SettingsProvider = ({ children }) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(loadCached);
  const [loaded, setLoaded] = useState(false);

  // Load from API whenever user logs in (user goes from null -> object)
  useEffect(() => {
    if (!user) {
      setSettings(DEFAULTS);
      setLoaded(false);
      return;
    }
    settingsAPI.get()
      .then(setts => {
        const merged = { ...DEFAULTS, ...setts };
        setSettings(merged);
        saveCache(merged);
        setLoaded(true);

        // ── Auto-sync timezone ─────────────────────────────────────────
        const storedTz = merged.timezone || 'auto';
        const needsTzSync = storedTz === 'auto' || storedTz === '' || /^GMT[+-]/.test(storedTz);
        const ianaTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

        // ── Auto-adjust Hijri offset once per device ───────────────────
        // Only applies if the user hasn't manually set it on this device.
        const hijriAutoSet = localStorage.getItem(HIJRI_AUTO_KEY);
        const suggestedOffset = getHijriOffsetForTimezone(ianaTz);
        // Auto-apply timezone-based Hijri offset ONLY if:
        // 1. Not already applied on this device (hijriAutoSet not in localStorage)
        // 2. The timezone actually suggests a non-default offset (not 0)
        // 3. The user hasn't already manually set their preference (stored value is still default 0)
        // This prevents accidentally overriding a user's manual +1 from Riyadh settings on a new device.
        const shouldAutoHijri = !hijriAutoSet && suggestedOffset !== 0 && merged.hijriAdjustment === 0;

        if (needsTzSync || shouldAutoHijri) {
          const updates = {};
          if (needsTzSync) updates.timezone = ianaTz;
          if (shouldAutoHijri) {
            updates.hijriAdjustment = suggestedOffset;
            localStorage.setItem(HIJRI_AUTO_KEY, '1');
          }
          settingsAPI.update(updates).then(updated => {
            const remerged = { ...DEFAULTS, ...updated };
            setSettings(remerged);
            saveCache(remerged);
          }).catch(() => {});
        }
      })
      .catch(() => setLoaded(true));
  }, [user]);

  // Update a single setting key: optimistic update + API call with revert on failure
  const updateSetting = useCallback(async (key, value) => {
    // If user manually sets hijriAdjustment → lock it so auto-adjust won't override again
    if (key === 'hijriAdjustment') {
      localStorage.setItem(HIJRI_AUTO_KEY, '1');
    }
    setSettings(prev => {
      const next = { ...prev, [key]: value };
      saveCache(next);
      return next;
    });
    try {
      const updated = await settingsAPI.update({ [key]: value });
      const merged = { ...DEFAULTS, ...updated };
      setSettings(merged);
      saveCache(merged);
    } catch (err) {
      console.error('Failed to save setting:', err);
      settingsAPI.get().then(setts => {
        const merged = { ...DEFAULTS, ...setts };
        setSettings(merged);
        saveCache(merged);
      }).catch(() => {});
    }
  }, []);

  // Force-refresh from API
  const refreshSettings = useCallback(async () => {
    try {
      const setts = await settingsAPI.get();
      const merged = { ...DEFAULTS, ...setts };
      setSettings(merged);
      saveCache(merged);
    } catch { /* ignore */ }
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, updateSetting, refreshSettings, loaded }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
